import { cx, fontSize, lineHeight, radius as radiusVar, size as sizeVar, type FontToken, type RadiusToken, type SizeToken } from "./tokens.ts";

export type SkeletonProps = {
  /** "text" (default): lines of a text style; "rect": a block; "circle": an avatar. */
  shape?: "text" | "rect" | "circle";
  /** text: the style whose line height each line takes (default body). */
  variant?: FontToken;
  /** text: number of lines (default 1); the last of several is shorter. */
  lines?: number;
  /** text / rect: fixed width (default: fills the row). */
  width?: SizeToken;
  /** rect: height (default avatar.lg). */
  height?: SizeToken;
  /** circle: diameter (default avatar.md). */
  size?: SizeToken;
  /** rect: corner radius (default md). */
  radius?: RadiusToken;
  testID?: string;
};

/**
 * Placeholder shape while content loads (a soft pulse; still under reduced motion). Hidden from assistive tech: the
 * loading region says it is busy (e.g. a Spinner or `aria-busy` on the list).
 */
export function Skeleton({ shape = "text", variant = "body", lines = 1, width, height, size, radius, testID }: SkeletonProps) {
  if (shape === "circle") {
    const d = sizeVar(size ?? "avatar.md");
    return <span className="b-skeleton b-skeleton-circle" aria-hidden data-testid={testID} style={{ width: d, height: d }} />;
  }
  if (shape === "rect") {
    return (
      <span
        className="b-skeleton b-skeleton-rect"
        aria-hidden
        data-testid={testID}
        style={{ width: width === undefined ? undefined : sizeVar(width), height: sizeVar(height ?? "avatar.lg"), borderRadius: radiusVar(radius ?? "md") }}
      />
    );
  }
  const n = Math.max(1, Math.floor(lines));
  return (
    <span className="b-skeleton-text" aria-hidden data-testid={testID} style={{ width: width === undefined ? undefined : sizeVar(width) }}>
      {Array.from({ length: n }, (_, i) => (
        <span key={i} className="b-skeleton-line" style={{ height: lineHeight(variant) }}>
          <span className={cx("b-skeleton", "b-skeleton-bar", n > 1 && i === n - 1 && "b-skeleton-bar-last")} style={{ height: fontSize(variant) }} />
        </span>
      ))}
    </span>
  );
}
